import { useRouter } from 'next/router';

import { FieldSet, FormHelp, Label, LabeledControl, Legend, Radio } from '@zillow/constellation-10';

import { LeaseDuration } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyEditV2WebApiHandler_leaseDuration } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { setListingField } from '../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';

import { LEASE_DURATION, LEASE_DURATION_CODE } from './constants';

const inputName = 'leaseDuration';

const LeaseDurationFormField = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const initialValue = listingDetails?.leaseDuration ? LEASE_DURATION[listingDetails?.leaseDuration] : undefined;

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_leaseDuration,
    applyToCache: ({ postData }) =>
      postData.leaseDuration ? [setListingField('leaseDuration', LEASE_DURATION_CODE[postData.leaseDuration])] : [],
    meta: { context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_leaseDuration' } },
  });

  const field = useFieldSave({
    fieldName: inputName,
    initialValue,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: { listingAlias, leaseDuration: value as LeaseDuration },
    }),
    autoSaveOnChange: true,
  });

  return (
    <div data-testid="LeaseDurationFormField">
      <FieldSet
        legend={<Legend required>Lease duration</Legend>}
        error={!!field.error}
        description={<FormHelp>{field.error}</FormHelp>}
      >
        <LabeledControl
          controlId="1mo"
          label={<Label>1 month</Label>}
          control={
            <Radio
              name={inputName}
              value={LeaseDuration.ONE_MONTH}
              checked={field.value === LeaseDuration.ONE_MONTH}
              onChange={(e) => field.onChange(e.target.value as LeaseDuration)}
            />
          }
        />
        <LabeledControl
          controlId="6mo"
          label={<Label>6 months</Label>}
          control={
            <Radio
              name={inputName}
              value={LeaseDuration.SIX_MONTH}
              checked={field.value === LeaseDuration.SIX_MONTH}
              onChange={(e) => field.onChange(e.target.value as LeaseDuration)}
            />
          }
        />
        <LabeledControl
          controlId="1y"
          label={<Label>1 year</Label>}
          control={
            <Radio
              name={inputName}
              value={LeaseDuration.ONE_YEAR}
              checked={field.value === LeaseDuration.ONE_YEAR}
              onChange={(e) => field.onChange(e.target.value as LeaseDuration)}
            />
          }
        />
        <LabeledControl
          controlId="r2o"
          label={<Label>Rent-to-own</Label>}
          control={
            <Radio
              name={inputName}
              value={LeaseDuration.RENT_TO_OWN}
              checked={field.value === LeaseDuration.RENT_TO_OWN}
              onChange={(e) => field.onChange(e.target.value as LeaseDuration)}
            />
          }
        />
        <LabeledControl
          controlId="sub"
          label={<Label>Sublet/temporary</Label>}
          control={
            <Radio
              name={inputName}
              value={LeaseDuration.SUBLET_TEMPORARY}
              checked={field.value === LeaseDuration.SUBLET_TEMPORARY}
              onChange={(e) => field.onChange(e.target.value as LeaseDuration)}
            />
          }
        />
      </FieldSet>
    </div>
  );
};

export default LeaseDurationFormField;
